/**
 * Accessibility Enhancements
 * Dynamically adds ARIA attributes and improves accessibility across the site
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccessibility);
  } else {
    initAccessibility();
  }

  function initAccessibility() {
    fixIconAccessibility();
    fixButtonAccessibility();
    fixLinkAccessibility();
    fixFormAccessibility();
    fixImageAccessibility();

    // Re-run fixes after AJAX/dynamic content loads
    observeDOMChanges();
  }

  /**
   * Fix SVG Icon Accessibility
   * Adds aria-hidden to decorative icons or aria-label to meaningful ones
   */
  function fixIconAccessibility() {
    const svgs = document.querySelectorAll('svg:not([role]):not([aria-hidden]):not([aria-label])');

    svgs.forEach(svg => {
      const parent = svg.parentElement;
      const hasText = parent && parent.textContent.trim().length > svg.textContent.trim().length;

      // If icon is next to text or inside a button/link with text, mark as decorative
      if (hasText || isDecorativeIcon(svg)) {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      } else if (isStandaloneIcon(svg)) {
        // Try to determine the purpose from context
        const ariaLabel = getIconLabel(svg, parent);
        if (ariaLabel) {
          svg.setAttribute('role', 'img');
          svg.setAttribute('aria-label', ariaLabel);
        } else {
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
        }
      }
    });
  }

  /**
   * Fix Button Accessibility
   * Adds aria-labels to icon-only buttons
   */
  function fixButtonAccessibility() {
    const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');

    buttons.forEach(button => {
      const text = button.textContent.trim();
      const hasIcon = button.querySelector('svg, i[class*="fa-"], .icon');

      // If button has no text but has an icon
      if (!text && hasIcon) {
        const label = getButtonLabel(button);
        if (label) {
          button.setAttribute('aria-label', label);
        }
      }

      // Ensure button has minimum size for touch targets
      if (!button.style.minWidth) {
        button.style.minWidth = '44px';
      }
      if (!button.style.minHeight) {
        button.style.minHeight = '44px';
      }
    });
  }

  /**
   * Fix Link Accessibility
   * Adds aria-labels to links without text
   */
  function fixLinkAccessibility() {
    const links = document.querySelectorAll('a:not([aria-label]):not([aria-labelledby])');

    links.forEach(link => {
      const text = link.textContent.trim();
      const hasIcon = link.querySelector('svg, i[class*="fa-"], .icon, img');

      // If link has no text but has an icon or image
      if ((!text || text.length < 2) && hasIcon) {
        const label = getLinkLabel(link);
        if (label) {
          link.setAttribute('aria-label', label);
        }
      }

      // Add warning for links that open in new windows
      if (link.target === '_blank' && !link.getAttribute('aria-label')?.includes('new window')) {
        const currentLabel = link.getAttribute('aria-label') || link.textContent.trim();
        if (currentLabel) {
          link.setAttribute('aria-label', `${currentLabel} (opens in new window)`);
        }
      }
    });
  }

  /**
   * Fix Form Accessibility
   * Ensures form inputs have associated labels
   */
  function fixFormAccessibility() {
    const inputs = document.querySelectorAll('input:not([type="hidden"]):not([aria-label]):not([aria-labelledby]), textarea:not([aria-label]):not([aria-labelledby])');

    inputs.forEach(input => {
      // Check if input already has an associated label
      const hasLabel = input.id && document.querySelector(`label[for="${input.id}"]`);

      if (!hasLabel) {
        // Try to find label by proximity or context
        const label = getInputLabel(input);
        if (label) {
          input.setAttribute('aria-label', label);
        }
      }

      // Add aria-required for required fields
      if (input.required && !input.hasAttribute('aria-required')) {
        input.setAttribute('aria-required', 'true');
      }
    });
  }

  /**
   * Fix Image Accessibility
   * Ensures images have alt text
   */
  function fixImageAccessibility() {
    const images = document.querySelectorAll('img:not([alt])');

    images.forEach(img => {
      // Check if image is decorative (in background, purely decorative class, etc.)
      if (isDecorativeImage(img)) {
        img.setAttribute('alt', '');
        img.setAttribute('role', 'presentation');
      } else {
        // Try to get meaningful alt text from context
        const altText = getImageAltText(img);
        img.setAttribute('alt', altText || 'Image');
      }
    });
  }

  /**
   * Helper Functions
   */

  function isDecorativeIcon(svg) {
    const decorativeClasses = ['icon-arrow', 'icon-chevron', 'icon-close', 'icon-menu', 'icon-search', 'icon-cart', 'icon-user', 'icon-wishlist'];
    const className = svg.getAttribute('class') || '';
    return decorativeClasses.some(cls => className.includes(cls));
  }

  function isStandaloneIcon(svg) {
    const parent = svg.parentElement;
    if (!parent) return false;

    const isButton = parent.tagName === 'BUTTON' || parent.tagName === 'A';
    const hasOnlyIcon = parent.children.length === 1 && parent.textContent.trim() === '';

    return isButton && hasOnlyIcon;
  }

  function getIconLabel(svg, parent) {
    const iconClass = svg.getAttribute('class') || '';
    const id = svg.getAttribute('id') || '';

    // Common icon mappings
    const iconMap = {
      'search': 'Search',
      'cart': 'Shopping cart',
      'user': 'Account',
      'menu': 'Menu',
      'close': 'Close',
      'arrow': 'Arrow',
      'chevron': 'More',
      'wishlist': 'Wishlist',
      'heart': 'Favorite',
      'star': 'Rating',
      'check': 'Check',
      'tick': 'Confirmed',
      'play': 'Play',
      'pause': 'Pause'
    };

    for (const [key, label] of Object.entries(iconMap)) {
      if (iconClass.includes(key) || id.includes(key)) {
        return label;
      }
    }

    // Check parent for hints
    if (parent) {
      const title = parent.getAttribute('title');
      if (title) return title;

      const dataLabel = parent.getAttribute('data-label') || parent.getAttribute('data-title');
      if (dataLabel) return dataLabel;
    }

    return null;
  }

  function getButtonLabel(button) {
    const title = button.getAttribute('title');
    if (title) return title;

    const className = button.getAttribute('class') || '';

    // Common button class mappings
    const buttonMap = {
      'quick-add': 'Quick add to cart',
      'add-to-cart': 'Add to cart',
      'close': 'Close',
      'menu': 'Open menu',
      'search': 'Search',
      'submit': 'Submit',
      'wishlist': 'Add to wishlist',
      'compare': 'Compare',
      'cart': 'View cart',
      'checkout': 'Checkout',
      'filter': 'Filter',
      'sort': 'Sort',
      'next': 'Next',
      'prev': 'Previous',
      'play': 'Play',
      'pause': 'Pause'
    };

    for (const [key, label] of Object.entries(buttonMap)) {
      if (className.includes(key)) {
        return label;
      }
    }

    return null;
  }

  function getLinkLabel(link) {
    const href = link.getAttribute('href') || '';
    const className = link.getAttribute('class') || '';
    const title = link.getAttribute('title');

    if (title) return title;

    // Check for social media links
    const socialMap = {
      'facebook': 'Facebook',
      'twitter': 'Twitter',
      'instagram': 'Instagram',
      'pinterest': 'Pinterest',
      'youtube': 'YouTube',
      'tiktok': 'TikTok',
      'linkedin': 'LinkedIn'
    };

    for (const [key, label] of Object.entries(socialMap)) {
      if (href.includes(key) || className.includes(key)) {
        return `Visit our ${label} page`;
      }
    }

    // Check for common link types
    if (className.includes('product') || href.includes('/products/')) {
      const img = link.querySelector('img');
      if (img && img.alt) return `View ${img.alt}`;
      return 'View product';
    }

    if (href.includes('collection')) return 'View collection';
    if (href.includes('cart')) return 'View cart';
    if (href.includes('account')) return 'My account';

    return null;
  }

  function getInputLabel(input) {
    const placeholder = input.getAttribute('placeholder');
    if (placeholder) return placeholder;

    const type = input.getAttribute('type');
    const name = input.getAttribute('name') || '';

    // Common input name mappings
    const inputMap = {
      'email': 'Email address',
      'search': 'Search',
      'quantity': 'Quantity',
      'password': 'Password',
      'firstname': 'First name',
      'lastname': 'Last name',
      'phone': 'Phone number',
      'address': 'Address',
      'city': 'City',
      'zip': 'ZIP code',
      'country': 'Country'
    };

    for (const [key, label] of Object.entries(inputMap)) {
      if (name.includes(key) || type === key) {
        return label;
      }
    }

    return null;
  }

  function isDecorativeImage(img) {
    const src = img.getAttribute('src') || '';
    const className = img.getAttribute('class') || '';

    // Check if image is a background pattern, spacer, or decorative element
    return src.includes('spacer') ||
           src.includes('placeholder') ||
           className.includes('decorative') ||
           className.includes('background');
  }

  function getImageAltText(img) {
    const src = img.getAttribute('src') || '';
    const title = img.getAttribute('title');

    if (title) return title;

    // Try to extract from filename
    const filename = src.split('/').pop().split('?')[0].split('.')[0];
    return filename.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Observe DOM changes for dynamically loaded content
   */
  function observeDOMChanges() {
    const observer = new MutationObserver(mutations => {
      let shouldRerun = false;

      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          shouldRerun = true;
        }
      });

      if (shouldRerun) {
        // Debounce the fixes
        clearTimeout(window.accessibilityTimeout);
        window.accessibilityTimeout = setTimeout(() => {
          fixIconAccessibility();
          fixButtonAccessibility();
          fixLinkAccessibility();
          fixFormAccessibility();
          fixImageAccessibility();
        }, 500);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

})();
